import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPivotPointEntryConditions } from '../../api/tradeGroups';
import { useAutoRefreshTrigger } from '../../stores/useGlobalAutoRefreshStore';
import './PivotPointEntryConditionsBar.css';

/**
 * PivotPointEntryConditionsBar (UI-PPEC-001).
 *
 * Pivot entry is gated on a discrete BULLISH_BOUNCE event, so — unlike the price-band strategies —
 * this bar leads with the proximity to the nearest enabled support level (the "nearing entry"
 * readout) and evaluates the ambient conditions (RSI / volume / regime) continuously, treating
 * "confirmed bounce" as one condition among the five rather than a gate that blanks the rest.
 *
 * Each of the five conditions renders as a strict boolean here: met ("✓", green) or not met
 * ("✗", yellow). The API deliberately stays tri-state on the wire — `true` / `false` / `null`,
 * where `null` means "input not yet evaluable" (e.g. no bounce yet for signal strength) — so the
 * operator can still distinguish "not met" from "not evaluated" by inspecting the response object
 * (see spec REQ-003). The UI collapses `null` → not-met at render time only, because from the
 * operator's glance an unevaluated condition and a failed condition are equally "entry not ready".
 *
 * Owns its own fetch and shares the `['pivot-entry-conditions', tradeGroupId, refreshTrigger]`
 * queryKey with {@link PivotPointExitConditionsBar} so TanStack dedupes to one HTTP call.
 */
export interface PivotPointEntryConditionsBarProps {
  tradeGroupId: string;
  className?: string;
}

const STALE_TIME_MS = 30_000;

type TriState = boolean | null | undefined;

// The wire value is tri-state (true / false / null) but each condition renders as a strict
// boolean: null ("not yet evaluable") collapses to not-met, so the operator never sees a "?".
// See the component docstring for why the API stays tri-state while the UI does not.
const statusFor = (value: TriState): { cls: string; symbol: string; word: string } => {
  if (value) return { cls: 'met', symbol: '✓', word: 'met' };
  return { cls: 'unmet', symbol: '✗', word: 'not met' };
};

const formatPct = (fraction: number | null | undefined): string => {
  if (fraction === null || fraction === undefined) return '—';
  const pct = fraction * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
};

interface ConditionSpec {
  key: string;
  label: string;
  value: TriState;
}

const PivotPointEntryConditionsBar: React.FC<PivotPointEntryConditionsBarProps> = ({
  tradeGroupId,
  className = '',
}) => {
  const refreshTrigger = useAutoRefreshTrigger('trade-groups');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['pivot-entry-conditions', tradeGroupId, refreshTrigger],
    queryFn: () => getPivotPointEntryConditions(tradeGroupId),
    staleTime: STALE_TIME_MS,
    retry: 1,
  });

  if (isError) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={`pivot-entry-conditions-bar loading ${className}`}>
        <div className="pivot-entry-gauge" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const {
    entry_conditions,
    nearest_support_level,
    nearest_support_distance_pct,
    confidence_score,
    min_confidence_score,
    is_cooldown_active,
  } = data;

  const conditions: ConditionSpec[] = [
    { key: 'strength', label: 'Strength', value: entry_conditions?.is_signal_strength_ok },
    { key: 'rsi', label: 'RSI', value: entry_conditions?.is_rsi_oversold },
    { key: 'volume', label: 'Volume', value: entry_conditions?.is_volume_surge },
    { key: 'regime', label: 'Regime', value: entry_conditions?.is_favorable_regime },
    { key: 'bounce', label: 'Bounce', value: entry_conditions?.is_confirmed_bounce },
  ];

  const metCount = entry_conditions?.conditions_met_count ?? 0;
  const confidenceMet = confidence_score !== null && confidence_score >= min_confidence_score;

  return (
    <div className={`pivot-entry-conditions-bar ${className}`}>
      <div className="pivot-entry-header-row">
        <span className="pivot-entry-label">Entry Conditions:</span>
        <span className="pivot-entry-summary" role="status" aria-label={`${metCount} of 5 entry conditions met`}>
          {metCount}/5 met
        </span>
        <span
          className="pivot-entry-proximity"
          title="Distance from current price to the nearest enabled support level"
          aria-label={`Nearest support ${nearest_support_level ?? 'unknown'}, distance ${formatPct(nearest_support_distance_pct)}`}
        >
          {nearest_support_level ?? '—'} {formatPct(nearest_support_distance_pct)}
        </span>
      </div>

      <div className="pivot-entry-gauge" role="img" aria-label="Pivot entry conditions — five segments">
        {conditions.map(c => {
          const { cls, symbol, word } = statusFor(c.value);
          return (
            <div
              key={c.key}
              className={`pivot-entry-segment ${cls}`}
              title={`${c.label}: ${word}`}
              aria-label={`${c.label}: ${word}`}
            >
              <span className="pivot-entry-segment-symbol">{symbol}</span>
              <span className="pivot-entry-segment-label">{c.label}</span>
            </div>
          );
        })}
      </div>

      <div className="pivot-entry-footer-row">
        <div className="pivot-entry-footer-cell">
          <span className="pivot-entry-footer-label">Confidence</span>
          <span className={`pivot-entry-footer-value ${confidenceMet ? 'met' : ''}`}>
            {confidence_score === null || confidence_score === undefined
              ? '—'
              : `${confidence_score}/${min_confidence_score}`}
          </span>
        </div>
        {is_cooldown_active && (
          <span className="pivot-entry-cooldown" role="status" aria-label="Failed-bounce cooldown active">
            Cooldown
          </span>
        )}
      </div>
    </div>
  );
};

export default PivotPointEntryConditionsBar;
